import 'src/app/utils/observable.extensions'; // just in case auth interceptor loads before app component
import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { debounceTime, filter, map, retry, switchMap, take, takeUntil } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { exists } from '../../functions/exists';
import { Session } from '../../models/account/dto/session';
import { AuthInterceptorService } from './auth-interceptor.service';
import { RegexUtils } from '../../utils/regex-utils';
import { ApiOriginUtils } from '../../utils/api-origin-utils';

/**
 * Do NOT directly inject UserDomainModel into the interceptor, because it will create circular injection errors.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    // decouple user domain model from auth interceptor using intermediary service
    private authInterceptorService: AuthInterceptorService
  ) {}

  /**
   * Hard cap on 403-driven retries so a token the backend keeps rejecting (e.g. an endpoint that 403s even after a
   * successful refresh) can't drive an unbounded refresh/retry loop. After this many retries the 403 propagates.
   */
  private readonly MAX_403_RETRIES = 3;

  // Matched against the end of the request pathname, so each path must be listed exactly
  private ignoreAuthTokenFor = [
    '/sign-in',
    '/sign-in-new-password',
    '/forgot-password-code',
    '/resend-code',
    '/reset-forgotten-password',
    '/refresh-session',
    '/sign-out'
  ];

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.shouldAttachAuthHeaders(request)) {
      return next.handle(request);
    }
    const pathname = ApiOriginUtils.pathnameOf(request.url);
    if (this.ignoreAuthTokenFor.some(path => pathname?.endsWith(path))) {
      return this.createHeaders$('', request.headers).pipe(
        take(1),
        map(headers => request.clone({ headers })),
        switchMap(req => next.handle(req))
      );
    }
    return this.waitAndAddValidAuthenticationToken$(request).pipe(
      take(1),
      switchMap(req => next.handle(req)),
      retry(this.error403RetryLogic()),
      takeUntil(this.authInterceptorService.destroySession$)
    );
  }

  /**
   * Only BudSense API origins get auth headers. The one off-API exception is the CDN cookie bootstrap (`GET /auth`),
   * which needs Token/ClientSecret. The CDN cookie clear (`DELETE /auth`) stays header-free and never waits on a token.
   */
  private shouldAttachAuthHeaders(request: HttpRequest<any>): boolean {
    return ApiOriginUtils.isBudSenseApiUrl(request?.url) || this.isCdnAuthBootstrapRequest(request);
  }

  private isCdnAuthBootstrapRequest(request: HttpRequest<any>): boolean {
    return (
      request?.method?.toUpperCase() === 'GET' &&
      ApiOriginUtils.originOf(request?.url) === ApiOriginUtils.originOf(environment.cdnUrl) &&
      RegexUtils.cdnAuthPath.test(ApiOriginUtils.pathnameOf(request?.url))
    );
  }

  /**
   * 403 errors mean that we tried to make an API call with an invalid token. Catch these specific errors with retry()
   * and wait for a valid token before making the request again.
   */
  private error403RetryLogic(): { count: number; delay: (error: any) => any } {
    return {
      count: this.MAX_403_RETRIES,
      delay: (error: any) => {
        if (error?.status === 403) {
          this.authInterceptorService.received403SoUpdateSessionTokenToRefreshing();
          return this.authInterceptorService.validSessionToken$.pipe(
            debounceTime(1000),
            filter(it => exists(it) && it !== Session.REFRESHING_AUTH_TOKEN_FROM_403),
            take(1)
          );
        }
        return throwError(() => error);
      }
    };
  }

  waitAndAddValidAuthenticationToken$(request: HttpRequest<any>): Observable<HttpRequest<any>> {
    return this.authInterceptorService.validSessionToken$.pipe(
      filter(it => exists(it) && it !== Session.REFRESHING_AUTH_TOKEN_FROM_403),
      take(1),
      switchMap(token => this.createHeaders$(token, request.headers)),
      take(1),
      map(headers => request.clone({ headers })),
      takeUntil(this.authInterceptorService.destroySession$)
    );
  }

  createHeaders$(token: string, headers: HttpHeaders): Observable<HttpHeaders> {
    return this.authInterceptorService.user$.pipe(
      take(1),
      map(user => {
        const append = (n: string, val: string | string[]) => {
          if (exists(val)) headers = headers.append(n, val);
        };
        // noinspection FallThroughInSwitchStatementJS - we want to check all cases
        switch (true) {
          case !headers.get('Content-Type'):
            append('Content-Type', 'application/json');
          case !headers.get('ClientSecret'):
            append('ClientSecret', environment?.cognitoClientSecret);
          case !headers.get('CompanyId'):
            append('CompanyId', user?.companyId?.toString());
          case !headers.get('Accept'):
            append('Accept', 'application/json');
          case !headers.get('UserId'):
            append('UserId', user?.userId);
          case !headers.get('Token'):
            append('Token', token);
        }
        return headers;
      })
    );
  }
}
